// Game schedule data — pick'em and bracket reference only.
// ESPN API attempted first; falls back to MOCK_GAMES on failure.
// Odds (spread + moneyline) fetched from The Odds API when key is present.

import { log }      from './log'
import { supabase } from './supabase'

const ESPN_BASE  = 'https://site.api.espn.com/apis/site/v2/sports'
const ESPN_PATHS = {
  NBA: 'basketball/nba', NFL: 'americanfootball/nfl',
  MLB: 'baseball/mlb',  NHL: 'icehockey/nhl',
}

const API_URL   = import.meta.env.VITE_API_URL || 'http://localhost:3001'
// Odds API is proxied through the backend (/api/odds/:sport). The key
// (ODDS_API_KEY) lives on Railway, not in the Vite bundle. The set below
// is a membership check so getGames doesn't issue a proxy call for sports
// with no Odds API coverage (UFC, Golf). server/index.js owns the
// authoritative sport-key mapping used to build the upstream URL.
const ODDS_SUPPORTED_SPORTS = new Set(['NBA', 'MLB', 'NHL', 'NFL'])

export function fmtML(ml) {
  if (ml == null) return ''
  return ml > 0 ? `+${ml}` : `${ml}`
}

const _now = Date.now()
const H    = 3_600_000
const M    = 60_000
function ts(h) { return _now + h * H }

// ── NBA Round 1 games (April 2026 playoffs) ───────────────────────────────────
// round / roundName / roundPts drive playoff pick'em scoring.
// All are Round 1 (1pt per correct pick).

const _NBA = [
  {
    id:'nba_g1', sport:'NBA', status:'final',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'New York Knicks',     abbr:'NYK', score:112, record:'2-0' },
    home:{ team:'Cleveland Cavaliers', abbr:'CLE', score:104, record:'0-2' },
    startTime:'6:30 PM ET', startTimeMs:ts(-4), spread:-2.5, awayML:-140, homeML:+118, total:218.5,
    seriesInfo:'NYK leads 2-0',
  },
  {
    id:'nba_g2', sport:'NBA', status:'live', period:'3rd Q', clock:'4:23',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Boston Celtics', abbr:'BOS', score:98, record:'2-1' },
    home:{ team:'Miami Heat',     abbr:'MIA', score:88, record:'1-2' },
    startTime:'7:00 PM ET', startTimeMs:ts(-1.5), spread:-4.5, awayML:-205, homeML:+172, total:214.0,
    seriesInfo:'BOS leads 2-1',
  },
  {
    id:'nba_g3', sport:'NBA', status:'upcoming',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Milwaukee Bucks', abbr:'MIL', score:null, record:'1-2' },
    home:{ team:'Indiana Pacers',  abbr:'IND', score:null, record:'2-1' },
    startTime:'8:30 PM ET', startTimeMs:ts(0.12),
    spread:1.5, awayML:+120, homeML:-142, total:228.5, seriesInfo:'IND leads 2-1',
  },
  {
    id:'nba_g4', sport:'NBA', status:'upcoming',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Denver Nuggets', abbr:'DEN', score:null, record:'2-1' },
    home:{ team:'LA Lakers',      abbr:'LAL', score:null, record:'1-2' },
    startTime:'9:30 PM ET', startTimeMs:ts(2.5), spread:-3.5, awayML:-175, homeML:+148, total:224.5,
    seriesInfo:'DEN leads 2-1',
  },
  {
    id:'nba_g5', sport:'NBA', status:'upcoming',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'OKC Thunder',          abbr:'OKC', score:null, record:'3-0' },
    home:{ team:'New Orleans Pelicans', abbr:'NOP', score:null, record:'0-3' },
    startTime:'10:00 PM ET', startTimeMs:ts(3), spread:-6, awayML:-265, homeML:+218, total:222.0,
    seriesInfo:'OKC leads 3-0',
  },
  {
    id:'nba_g6', sport:'NBA', status:'final',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Minnesota Timberwolves', abbr:'MIN', score:108, record:'2-1' },
    home:{ team:'Phoenix Suns',          abbr:'PHX', score:99,  record:'1-2' },
    startTime:'5:30 PM ET', startTimeMs:ts(-5), spread:-3.5, awayML:-175, homeML:+148, total:215.5,
    seriesInfo:'MIN leads 2-1',
  },
  {
    id:'nba_g7', sport:'NBA', status:'upcoming',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Golden State Warriors', abbr:'GSW', score:null, record:'1-2' },
    home:{ team:'LA Clippers',           abbr:'LAC', score:null, record:'2-1' },
    startTime:'10:30 PM ET', startTimeMs:ts(3.5), spread:1.5, awayML:+120, homeML:-142, total:228.0,
    seriesInfo:'LAC leads 2-1',
  },
  {
    id:'nba_g8', sport:'NBA', status:'upcoming',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Atlanta Hawks',   abbr:'ATL', score:null, record:'1-2' },
    home:{ team:'Boston Celtics',  abbr:'BOS', score:null, record:'2-1' },
    startTime:'7:30 PM ET', startTimeMs:ts(1), spread:5.5, awayML:+210, homeML:-258, total:214.5,
    seriesInfo:'BOS leads 2-1',
  },
]

const _MLB = [
  {
    id:'mlb_g1', sport:'MLB', status:'live', period:'Bot 7th', clock:'',
    round:null, roundName:null, roundPts:1,
    away:{ team:'New York Yankees', abbr:'NYY', score:3, record:'16-8' },
    home:{ team:'Boston Red Sox',   abbr:'BOS', score:2, record:'14-10' },
    startTime:'7:05 PM ET', startTimeMs:ts(-2), spread:-1.5, awayML:-165, homeML:+140, total:8.5,
  },
  {
    id:'mlb_g2', sport:'MLB', status:'upcoming',
    round:null, roundName:null, roundPts:1,
    away:{ team:'LA Dodgers', abbr:'LAD', score:null, record:'18-6' },
    home:{ team:'SF Giants',  abbr:'SF',  score:null, record:'12-12' },
    startTime:'9:40 PM ET', startTimeMs:ts(2.75), spread:-1.5, awayML:-210, homeML:+175, total:7.5,
  },
  {
    id:'mlb_g3', sport:'MLB', status:'upcoming',
    round:null, roundName:null, roundPts:1,
    away:{ team:'Atlanta Braves', abbr:'ATL', score:null, record:'15-9' },
    home:{ team:'New York Mets',  abbr:'NYM', score:null, record:'13-11' },
    startTime:'7:10 PM ET', startTimeMs:ts(3.5), spread:-1.5, awayML:-180, homeML:+152, total:8.0,
  },
  {
    id:'mlb_g4', sport:'MLB', status:'final',
    round:null, roundName:null, roundPts:1,
    away:{ team:'Chicago Cubs',        abbr:'CHC', score:4, record:'11-13' },
    home:{ team:'St. Louis Cardinals', abbr:'STL', score:2, record:'13-11' },
    startTime:'6:45 PM ET', startTimeMs:ts(-3.5), spread:1.5, awayML:+138, homeML:-162, total:8.5,
  },
]

const _NHL = [
  {
    id:'nhl_g1', sport:'NHL', status:'live', period:'2nd', clock:'14:23',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Florida Panthers', abbr:'FLA', score:2, record:'2-1' },
    home:{ team:'Boston Bruins',    abbr:'BOS', score:1, record:'1-2' },
    startTime:'7:00 PM ET', startTimeMs:ts(-1.25), spread:-1.5, awayML:-180, homeML:+152, total:5.5,
    seriesInfo:'FLA leads 2-1',
  },
  {
    id:'nhl_g2', sport:'NHL', status:'upcoming',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Colorado Avalanche', abbr:'COL', score:null, record:'2-2' },
    home:{ team:'Edmonton Oilers',    abbr:'EDM', score:null, record:'2-2' },
    startTime:'9:00 PM ET', startTimeMs:ts(2), spread:-1.5, awayML:-155, homeML:+130, total:6.0,
    seriesInfo:'Series tied 2-2',
  },
  {
    id:'nhl_g3', sport:'NHL', status:'upcoming',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Toronto Maple Leafs', abbr:'TOR', score:null, record:'2-1' },
    home:{ team:'Tampa Bay Lightning', abbr:'TBL', score:null, record:'1-2' },
    startTime:'7:30 PM ET', startTimeMs:ts(3.5), spread:-1.5, awayML:-168, homeML:+142, total:5.5,
    seriesInfo:'TOR leads 2-1',
  },
  {
    id:'nhl_g4', sport:'NHL', status:'final',
    round:1, roundName:'First Round', roundPts:1,
    away:{ team:'Dallas Stars',        abbr:'DAL', score:4, record:'2-1' },
    home:{ team:'Vegas Golden Knights', abbr:'VGK', score:3, record:'1-2' },
    startTime:'8:00 PM ET', startTimeMs:ts(-3), spread:-1.5, awayML:-188, homeML:+158, total:6.0,
    seriesInfo:'DAL leads 2-1',
  },
]

export const MOCK_GAMES = [..._NBA, ..._MLB, ..._NHL]

// ── Playoff round metadata (used by playoff pick'em mode) ─────────────────────

export const PLAYOFF_ROUND_INFO = {
  NBA: [
    { round:1, id:'r1', name:'First Round',      pts:1, estimatedEnd:'Early May 2026' },
    { round:2, id:'r2', name:'Semifinals',        pts:2, estimatedEnd:'Mid May 2026'   },
    { round:3, id:'cf', name:'Conference Finals', pts:4, estimatedEnd:'Late May 2026'  },
    { round:4, id:'fin',name:'Finals',            pts:8, estimatedEnd:'June 22, 2026'  },
  ],
  NHL: [
    { round:1, id:'r1', name:'First Round',      pts:1, estimatedEnd:'Early May 2026' },
    { round:2, id:'r2', name:'Second Round',     pts:2, estimatedEnd:'Mid May 2026'   },
    { round:3, id:'cf', name:'Conference Finals',pts:4, estimatedEnd:'Late May 2026'  },
    { round:4, id:'fin',name:'Stanley Cup Final',pts:8, estimatedEnd:'June 15, 2026'  },
  ],
  MLB: [
    { round:1, id:'r1', name:'Wild Card',             pts:1, estimatedEnd:'Early October 2026' },
    { round:2, id:'r2', name:'Division Series',       pts:2, estimatedEnd:'Mid October 2026'   },
    { round:3, id:'cf', name:'Championship Series',   pts:4, estimatedEnd:'Late October 2026'  },
    { round:4, id:'fin',name:'World Series',          pts:8, estimatedEnd:'November 2026'       },
  ],
  NFL: [
    { round:1, id:'r1', name:'Wild Card',              pts:1, estimatedEnd:'Mid January 2027'  },
    { round:2, id:'r2', name:'Divisional',             pts:2, estimatedEnd:'Late January 2027' },
    { round:3, id:'cf', name:'Conference Championship',pts:4, estimatedEnd:'Late January 2027' },
    { round:4, id:'fin',name:'Super Bowl',             pts:8, estimatedEnd:'February 8, 2027'  },
  ],
}

// ── Playoff round helpers ─────────────────────────────────────────────────────

// ESPN round abbreviation → points value (R1=1, R2=2, CF=4, F=8)
export function getRoundPoints(abbr) {
  if (abbr === 'RD2') return 8
  if (abbr === 'RD4') return 4
  if (abbr === 'RD8') return 2
  return 1
}

function getRoundFromAbbr(abbr) {
  if (abbr === 'RD2')  return 4
  if (abbr === 'RD4')  return 3
  if (abbr === 'RD8')  return 2
  if (abbr === 'RD16') return 1
  return null
}

function getRoundNameFromAbbr(abbr, sport) {
  const round = getRoundFromAbbr(abbr)
  if (!round) return null
  return PLAYOFF_ROUND_INFO[sport]?.find(r => r.round === round)?.name ?? null
}

// ── Odds API helpers ──────────────────────────────────────────────────────────

// Module-level cache keyed by sport. The Odds API is now proxied through the
// backend (/api/odds/:sport), which owns the 1-hour quota-protecting TTL.
// The 5-min client cache below just absorbs the 60s game-poll cadence in
// App.jsx within a single session so we don't re-hit the proxy on every tick.
//
// `oddsApiDisabled` is a one-way latch. The proxy itself returns 200+JSON
// in all upstream-fault cases (401/403/429/network → []), so any non-OK
// from the proxy signals proxy or transport failure. Latching stops the
// 60s poll from flooding the console for the rest of the session; the UI
// still gets mock odds via fillMissingOdds downstream.
const oddsCache       = {}
const oddsCacheTime   = {}
const ODDS_CACHE_TTL  = 5 * 60 * 1000   // 5 minutes
let   oddsApiDisabled = false

async function fetchOdds(sport) {
  if (!ODDS_SUPPORTED_SPORTS.has(sport)) return []
  if (oddsApiDisabled) return []

  const cachedAt = oddsCacheTime[sport]
  if (cachedAt && Date.now() - cachedAt < ODDS_CACHE_TTL) {
    return oddsCache[sport] || []
  }

  try {
    const r = await fetch(
      `${API_URL}/api/odds/${sport}`,
      { signal: AbortSignal.timeout(4000) }
    )
    if (!r.ok) {
      oddsApiDisabled = true
      log('[odds] proxy rejected (status', r.status + ') — disabling Odds API for the rest of this session')
      return []
    }
    const data = await r.json()
    oddsCache[sport]     = data
    oddsCacheTime[sport] = Date.now()
    return data
  } catch (err) {
    log('[odds] fetch failed — falling back to mock odds:', err?.message || err)
    return []
  }
}

// Derive realistic-looking moneyline pair from a spread. Used when the Odds
// API returns nothing but we still want to show something on the card.
//   spread is the away-side line: -3.5 means the away team is favored by 3.5.
function spreadToML(spread) {
  if (spread == null) return { fav: -110, dog: -110 }
  const abs = Math.abs(spread)
  const favML = -(100 + (abs * 18))
  const dogML = Math.round(favML * -0.75)
  return { fav: Math.round(favML), dog: dogML }
}

// Default spreads per sport — used when ESPN didn't supply one and we still
// need *something* on the card so users see a price instead of dashes.
const DEFAULT_SPREAD_BY_SPORT = { NBA: -3.5, NHL: -1.5, MLB: -1.5, NFL: -3.5 }

// Backfills missing spread/ML on each game so the odds strip is never blank.
// Order of preference: real API odds (already merged) → derive ML from
// existing spread → fall back to sport-default mock.
function fillMissingOdds(games, sport) {
  if (!games?.length) return games
  const fallbackSpread = DEFAULT_SPREAD_BY_SPORT[sport] ?? -3.5
  return games.map(g => {
    const out = { ...g }
    let spread = out.spread
    if (spread == null) {
      // Use seed differential if available (playoff games have seeds), else
      // sport default.
      const aSeed = out.away?.seed, hSeed = out.home?.seed
      if (aSeed != null && hSeed != null && aSeed !== hSeed) {
        const diff = hSeed - aSeed
        spread = parseFloat((-(diff * 1.5)).toFixed(1))
      } else {
        spread = fallbackSpread
      }
      out.spread = spread
    }
    if (out.awayML == null || out.homeML == null) {
      const { fav, dog } = spreadToML(spread)
      // Negative spread = away is favored (matches spreadLabel convention).
      const awayIsFav = spread < 0
      if (out.awayML == null) out.awayML = awayIsFav ? fav : dog
      if (out.homeML == null) out.homeML = awayIsFav ? dog : fav
    }
    return out
  })
}

function mergeOdds(games, oddsEvents) {
  if (!oddsEvents?.length) return games
  return games.map(game => {
    const ev = oddsEvents.find(e =>
      normalizeESPNName(e.home_team) === game.home.team &&
      normalizeESPNName(e.away_team) === game.away.team
    )
    if (!ev) return game

    // Server proxy pre-shapes per-event prices (server/index.js
    // /api/odds/:sport picks the first bookmaker per market). We read
    // them flat. null on any field means the proxy couldn't source a
    // bookmaker for that market — fall back to whatever was already on
    // the game so fillMissingOdds downstream still has a chance to
    // synthesize. Matches the prior "bookmakers[0] lacks this market"
    // fallback behavior exactly.
    const spread = ev.spread != null ? ev.spread : game.spread
    const awayML = ev.awayML != null ? ev.awayML : (game.awayML ?? null)
    const homeML = ev.homeML != null ? ev.homeML : (game.homeML ?? null)
    const total  = ev.total  != null ? ev.total  : (game.total  ?? null)
    return { ...game, spread, awayML, homeML, total }
  })
}

// Returns { spread, awayML, homeML } derived from seed matchup for bracket display.
// topSeed is the higher seed (lower number = better seed).
export function getMatchupOdds(topSeed, botSeed) {
  if (topSeed == null || botSeed == null) return null
  const diff = botSeed - topSeed   // positive = top seed is better
  if (diff <= 0) return null       // equal or reversed seeds
  const favML  = diff >= 7 ? -380 : diff >= 5 ? -280 : diff >= 3 ? -195 : diff >= 2 ? -155 : -132
  const dogML  = Math.round(10000 / Math.abs(favML))
  const spread = parseFloat((-(diff * 1.8)).toFixed(1))
  return { spread, awayML: favML, homeML: +dogML }
}

// ── Public API ────────────────────────────────────────────────────────────────

// Converts a raw Odds API event into our game shape when ESPN is unavailable.
function oddsEventToGame(ev, sport) {
  if (!ev.home_team || !ev.away_team) return null
  const startMs  = new Date(ev.commence_time).getTime()
  const now      = Date.now()
  const awayName = normalizeESPNName(ev.away_team)
  const homeName = normalizeESPNName(ev.home_team)
  const abbr     = name => name.split(' ').pop().slice(0, 3).toUpperCase()

  // Server proxy pre-shapes per-event prices (server/index.js
  // /api/odds/:sport picks the first bookmaker per market). We read
  // them flat. Any market the server couldn't source a bookmaker for
  // arrives null and falls through to fillMissingOdds downstream the
  // same way the pre-rewrite code did when bookmakers[0] lacked a
  // market.
  const spread = ev.spread ?? null
  const awayML = ev.awayML ?? null
  const homeML = ev.homeML ?? null
  const total  = ev.total  ?? null

  const status = startMs < now - 3 * H ? 'final'
               : startMs < now         ? 'live'
               : 'upcoming'

  return {
    id: ev.id, sport, status,
    round: null, roundPts: 1,
    period: '', clock: '',
    away: { team: awayName, abbr: abbr(awayName), score: null, record: '' },
    home: { team: homeName, abbr: abbr(homeName), score: null, record: '' },
    startTime: new Date(startMs).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }),
    startTimeMs: startMs,
    spread, awayML, homeML, total,
  }
}

// ── Line movement tracking (sessionStorage) ───────────────────────────────────
// Snapshot the first spread we see for each game in this session, then compare
// future fetches to it so we can flag movement.

const LINE_KEY = 'wb_line_snapshot_v1'

function readSnapshot() {
  if (typeof sessionStorage === 'undefined') return {}
  try { return JSON.parse(sessionStorage.getItem(LINE_KEY) || '{}') } catch { return {} }
}
function writeSnapshot(snap) {
  if (typeof sessionStorage === 'undefined') return
  try { sessionStorage.setItem(LINE_KEY, JSON.stringify(snap)) } catch {}
}

// Mutates each game in-place to add `lineMovement` ('up' | 'down' | 'none')
// and `movementAmount` (absolute spread delta from session-open).
// 'up' = home spread moved toward favoring home (more public on home side).
function attachLineMovement(games) {
  if (!games?.length) return games
  const snap = readSnapshot()
  let dirty = false
  for (const g of games) {
    if (g.spread == null) {
      g.lineMovement = 'none'
      g.movementAmount = 0
      continue
    }
    const prev = snap[g.id]
    if (prev == null) {
      snap[g.id] = g.spread
      dirty = true
      g.lineMovement = 'none'
      g.movementAmount = 0
      continue
    }
    const delta = +(g.spread - prev).toFixed(1)
    g.movementAmount = Math.abs(delta)
    if (Math.abs(delta) < 0.5) g.lineMovement = 'none'
    // Spread is from the away team's perspective. A more-negative spread means
    // the away team is more favored. We surface direction relative to home:
    //   delta > 0 → spread became less negative → public moved toward home
    //   delta < 0 → spread became more negative → public moved toward away
    else if (delta > 0) g.lineMovement = 'up'   // home favored more
    else                g.lineMovement = 'down' // away favored more
  }
  if (dirty) writeSnapshot(snap)
  return games
}

// opts is optional. Today only opts.eventId is consumed (UFC, post Phase 4
// of the UFC-dropdown work — group.ufc_config.eventId). Non-UFC callers
// pass nothing; their behavior is unchanged. Keeping every sport routed
// through getGames preserves the invariant that callers don't need to
// special-case which lib function to call.
export async function getGames(sport = 'NBA', opts = {}) {
  if (sport === 'UFC') return getUFCFights(opts.eventId)
  const path = ESPN_PATHS[sport]
  const now  = Date.now()

  // ── ESPN scoreboard (via backend proxy to avoid CORS) ───────────────────────
  // null = call failed; [] = succeeded with no games scheduled
  let espnGames = null
  if (path) {
    try {
      const r = await fetch(`${API_URL}/api/espn/${sport.toLowerCase()}/scoreboard`, { signal: AbortSignal.timeout(5000) })
      if (r.ok) espnGames = parseESPN(await r.json(), sport)
    } catch { /* stays null */ }
  }

  // ── Odds API ────────────────────────────────────────────────────────────────
  const oddsData = await fetchOdds(sport)

  // ── Pick source — never mix real and mock ───────────────────────────────────
  let games
  const usedMock = espnGames === null && !oddsData.length

  if (espnGames !== null) {
    games = mergeOdds(espnGames, oddsData)
  } else if (oddsData.length) {
    games = oddsData.map(ev => oddsEventToGame(ev, sport)).filter(Boolean)
  } else {
    games = mockGames(sport)
  }

  // ── Filter to current window (last 24 h → next 48 h) and sort ──────────────
  // 24 h backward window so the Games tab's "RECENT" section can show finals
  // from the past day, not just the past few hours.
  const cutoff  = now - 24 * H
  const horizon = now + 48 * H
  const filtered = games
    .filter(g => {
      if (g.status === 'live') return true
      if (!g.startTimeMs)     return true
      return g.startTimeMs >= cutoff && g.startTimeMs <= horizon
    })
    .sort((a, b) => (a.startTimeMs ?? Infinity) - (b.startTimeMs ?? Infinity))

  // Backfill any missing spread/ML so the card never has a blank strip.
  const finalGames = attachLineMovement(fillMissingOdds(filtered, sport))

  // Temporary: dump ID/team/status shape so pick-grading mismatches can be
  // traced from Railway logs. Remove once grading is confirmed working.
  log('[getGames] sample game IDs:', finalGames.slice(0, 3).map(g => ({
    id: g.id, home: g.home?.team, away: g.away?.team, status: g.status,
  })))

  return finalGames
}

// Fetch games for a specific calendar date (YYYY-MM-DD or Date object).
// Uses the ?date param on the server proxy so ESPN returns only that day.
export async function getGamesByDate(sport, targetDate) {
  const sportKey = sport?.toLowerCase() || 'nba'
  const dateStr = targetDate instanceof Date
    ? targetDate.toISOString().slice(0, 10).replace(/-/g, '')
    : String(targetDate).replace(/-/g, '')
  try {
    const res = await fetch(
      `${API_URL}/api/espn/${sportKey}/scoreboard?date=${dateStr}`,
      { signal: AbortSignal.timeout(5000) }
    )
    if (!res.ok) return []
    return parseESPN(await res.json(), sport) || []
  } catch (err) {
    log('getGamesByDate failed:', err)
    return []
  }
}

function mockGames(sport) {
  return MOCK_GAMES.filter(g => g.sport === sport)
}

// Maps ESPN displayName values to the exact team name strings used in mock data
// and as keys in teamStats.js. Keeps the whole codebase on a single name convention.
const ESPN_NAME_MAP = {
  // NBA — ESPN uses full official city names
  'Oklahoma City Thunder':  'OKC Thunder',
  'Los Angeles Lakers':     'LA Lakers',
  'Los Angeles Clippers':   'LA Clippers',
  // MLB
  'Los Angeles Dodgers':    'LA Dodgers',
  'San Francisco Giants':   'SF Giants',
  // MLB — ParlayAPI emits "Oakland Athletics", ESPN ships just "Athletics"
  // post-relocation. Map the verbose odds-provider form to ESPN's canonical
  // so mergeOdds can reconcile A's games.
  'Oakland Athletics':      'Athletics',
  // Everything else already matches mock names; add more here if needed
}

function normalizeESPNName(displayName) {
  return ESPN_NAME_MAP[displayName] || displayName
}

function parseESPN(data, sport) {
  if (!data?.events?.length) return []
  return data.events.map(evt => {
    const comp   = evt.competitions?.[0]
    const home   = comp?.competitors?.find(c => c.homeAway === 'home')
    const away   = comp?.competitors?.find(c => c.homeAway === 'away')
    const sn     = comp?.status?.type?.name ?? ''
    const status = sn === 'STATUS_IN_PROGRESS' ? 'live' : sn === 'STATUS_FINAL' ? 'final' : 'upcoming'
    const statusState     = comp?.status?.type?.state || ''
    const statusDetail    = comp?.status?.type?.shortDetail || comp?.status?.type?.detail || ''
    const statusCompleted = comp?.status?.type?.completed || false
    const start  = new Date(comp?.date).getTime()

    // Playoff metadata — present only for playoff games (RD16/RD8/RD4/RD2)
    const roundAbbr   = comp?.type?.abbreviation || null
    const isPlayoff   = !!roundAbbr && roundAbbr.startsWith('RD')
    const roundPoints = isPlayoff ? getRoundPoints(roundAbbr) : 1
    const round       = isPlayoff ? getRoundFromAbbr(roundAbbr) : null
    const roundName   = isPlayoff ? getRoundNameFromAbbr(roundAbbr, sport) : null

    // Series record / game number — try ESPN's series object, fall back to notes headline
    const series   = comp?.series || null
    const headline = comp?.notes?.[0]?.headline || ''
    let gameNumber = series?.gameNumber ?? series?.value ?? null
    if (gameNumber == null) {
      const m = headline.match(/Game\s+(\d+)/i)
      if (m) gameNumber = parseInt(m[1])
    }
    const seriesRecord = series?.summary || null

    const awayName = normalizeESPNName(away?.team?.displayName ?? '')
    const homeName = normalizeESPNName(home?.team?.displayName ?? '')
    const seriesTeams = isPlayoff && awayName && homeName ? `${awayName} vs ${homeName}` : null

    const awaySeed = parseInt(away?.curatedRank?.current) || parseInt(away?.seed) || null
    const homeSeed = parseInt(home?.curatedRank?.current) || parseInt(home?.seed) || null

    // Live-game richness — PURELY ADDITIVE fields for the Games & Scores
    // redesign. `situation` is MLB live context (base runners + count);
    // null on all other games (other sports, non-live MLB, missing data).
    // `periodNumber` is the raw integer ESPN status.period (innings for MLB,
    // quarters for NBA/NFL, etc.) — already exposed as a formatted string
    // in the `period` field below; the raw int is kept here for displays
    // that want "9 Innings" math without re-parsing the string. Downstream
    // readers that don't know about these fields are unaffected — no
    // existing field changes, no rename, no restructure.
    let situation = null
    if (sport === 'MLB' && status === 'live' && comp?.situation) {
      const s = comp.situation
      situation = {
        balls:    s.balls    ?? null,
        strikes:  s.strikes  ?? null,
        outs:     s.outs     ?? null,
        onFirst:  !!s.onFirst?.athlete,
        onSecond: !!s.onSecond?.athlete,
        onThird:  !!s.onThird?.athlete,
      }
    }
    const periodNumber = comp?.status?.period ?? null

    return {
      id: evt.id, sport, status,
      // Raw ESPN status fields kept alongside the mapped `status` so
      // resolvePicks can detect "final" across multiple shapes.
      statusState,
      statusDetail,
      statusCompleted,
      // Flat score fields mirror the nested home.score / away.score so
      // resolvePicks can read either.
      homeScore: home?.score ?? null,
      awayScore: away?.score ?? null,
      round,
      roundAbbr,
      roundName,
      roundPts: roundPoints,
      roundPoints,
      gameNumber,
      seriesRecord,
      seriesInfo: seriesRecord,   // legacy alias used by existing UI
      seriesTeams,
      period: comp?.status?.period ? periodLabel(sport, comp.status.period) : '',
      clock: comp?.status?.displayClock ?? '',
      away: {
        team: awayName,
        abbr: away?.team?.abbreviation ?? '',
        color: away?.team?.color ?? null,
        alternateColor: away?.team?.alternateColor ?? null,
        score: away?.score ?? null,
        record: away?.records?.[0]?.summary ?? '',
        seed: awaySeed,
        // Additive: city/nickname split for the Games & Scores row layout
        // (mockup stacks "SAN DIEGO" / "PADRES"). Null on sources that
        // don't expose them (getUFCFights — fighters have no city).
        location: away?.team?.location ?? null,
        name: away?.team?.name ?? null,
      },
      home: {
        team: homeName,
        abbr: home?.team?.abbreviation ?? '',
        color: home?.team?.color ?? null,
        alternateColor: home?.team?.alternateColor ?? null,
        score: home?.score ?? null,
        record: home?.records?.[0]?.summary ?? '',
        seed: homeSeed,
        location: home?.team?.location ?? null,
        name: home?.team?.name ?? null,
      },
      startTime: status === 'upcoming'
        ? new Date(comp?.date).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' })
        : status === 'final' ? 'Final' : 'LIVE',
      startTimeMs: start,
      commenceTime: comp?.date || null,
      spread: null, awayML: null, homeML: null, total: null,
      // Additive — Games & Scores screen redesign. See comment above the
      // return for the full rationale. Null/absent on every non-applicable game.
      situation,
      periodNumber,
    }
  })
}

function periodLabel(sport, n) {
  if (sport === 'NBA' || sport === 'NFL') return `Q${n}`
  if (sport === 'NHL') return n <= 3 ? ['1st','2nd','3rd'][n-1] : `OT${n-3}`
  if (sport === 'MLB') { const s=['th','st','nd','rd']; const v=n%100; return n+(s[(v-20)%10]||s[v]||s[0])+' Inn' }
  return `P${n}`
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function isGameLocked(game) {
  if (game.status === 'live' || game.status === 'final') return true
  // ESPN's orthogonal `completed` flag (parseESPN line 569 → 602). True on
  // every terminal state regardless of which status name ESPN sends —
  // STATUS_END_GAME, STATUS_COMPLETED, etc. all surface as completed=true
  // even though parseESPN's status-name map normalizes them to 'upcoming'.
  // OR-added; cannot affect live/upcoming games. Undefined on game sources
  // that don't populate it (getUFCFights, oddsEventToGame, MOCK_GAMES) —
  // undefined === true is false, so those paths see no behavior change.
  if (game.statusCompleted === true) return true
  return Date.now() >= (game.startTimeMs ?? Infinity) - 10 * M
}

export function spreadLabel(spread, side) {
  if (spread == null) return ''
  const val = side === 'away' ? spread : -spread
  return val > 0 ? `+${val}` : `${val}`
}

// ── Sport availability ────────────────────────────────────────────────────────

const SPORT_AVAIL = {
  NBA:              { pickem:true,  nextGame:'Tonight', bracket:true,  playoffPickem:true,  bracketEnd:'June 22, 2026', bracketDays:62 },
  MLB:              { pickem:true,  nextGame:'Tonight', bracket:false, playoffPickem:false, bracketReason:'MLB playoffs start October 2026' },
  NHL:              { pickem:true,  nextGame:'Tonight', bracket:true,  playoffPickem:true,  bracketEnd:'June 15, 2026', bracketDays:55 },
  NFL:              { pickem:false, nextGame:'September 5, 2026', bracket:false, playoffPickem:false, bracketReason:'NFL season begins September 2026' },
  UFC:              { pickem:true,  nextGame:'Next UFC card',     bracket:false, playoffPickem:false, bracketReason:'UFC uses single-event card scoring' },
  Golf:             { pickem:true,  nextGame:'Next tournament',   bracket:false, playoffPickem:false, bracketReason:'Golf uses tournament-rounds scoring (see picker)' },
  'NCAA Basketball':{ pickem:false, nextGame:'November 2026',    bracket:false, playoffPickem:false, bracketReason:'March Madness ended April 7, 2026' },
  'NCAA Football':  { pickem:false, nextGame:'September 2026',   bracket:false, playoffPickem:false, bracketReason:'CFP begins January 2027' },
}

export function getPickemAvailability(sport, durationType = 'daily') {
  const info = SPORT_AVAIL[sport]
  if (!info) return { available:false, reason:`Unknown sport: ${sport}` }
  if (durationType === 'playoffs') {
    if (!info.playoffPickem) return { available:false, reason: info.bracketReason || `No ${sport} playoffs active` }
    return { available:true }
  }
  if (!info.pickem) return { available:false, reason:`No ${sport} games scheduled. Next game: ${info.nextGame}` }
  return { available:true, nextGame:info.nextGame }
}

export function getBracketAvailability(sport) {
  const info = SPORT_AVAIL[sport]
  if (!info) return { available:false, reason:`Unknown sport: ${sport}` }
  if (!info.bracket) return { available:false, reason: info.bracketReason || `No ${sport} playoffs available` }
  return { available:true, endDate:info.bracketEnd, days:info.bracketDays }
}

// Returns a { 'YYYY-MM-DD': count } map for the next `days` days. Used by the
// daily-group date picker to highlight the day with the most games scheduled.
// One fetch per date with ?date=YYYYMMDD — the no-date scoreboard endpoint
// only returns today's slate, so future days would otherwise read as 0.
export async function getGameCountsByDay(sport, days = 7) {
  const path = ESPN_PATHS[sport]
  if (!path) return {}
  const sportKey = sport.toLowerCase()
  const today = new Date(); today.setHours(0, 0, 0, 0)

  const targets = []
  for (let i = 0; i < days; i++) {
    const d = new Date(today); d.setDate(d.getDate() + i)
    targets.push({
      iso:     d.toISOString().slice(0, 10),
      dateStr: d.toISOString().slice(0, 10).replace(/-/g, ''),
    })
  }

  // For today's entry we only count games that are still pickable — start
  // is >10 minutes from now (matches the per-game lock window in
  // isGameLocked). Earlier games on today's slate have already locked, so
  // counting them inflates the picker dots and silently allows tie-prone
  // group creation. Future dates count every scheduled event since none
  // are locked yet.
  const todayIso = targets[0]?.iso
  const lockCutoffMs = Date.now() + 10 * 60 * 1000

  const results = await Promise.all(targets.map(async t => {
    try {
      const r = await fetch(
        `${API_URL}/api/espn/${sportKey}/scoreboard?date=${t.dateStr}`,
        { signal: AbortSignal.timeout(5000) }
      )
      if (!r.ok) return [t.iso, 0]
      const data = await r.json()
      const events = data?.events || []
      if (t.iso !== todayIso) return [t.iso, events.length]
      // Today: filter to events that haven't locked yet
      const pickable = events.filter(e => {
        const startMs = new Date(e.date || e.competitions?.[0]?.date || 0).getTime()
        return startMs >= lockCutoffMs
      })
      return [t.iso, pickable.length]
    } catch {
      return [t.iso, 0]
    }
  }))

  const counts = {}
  for (const [iso, n] of results) counts[iso] = n
  return counts
}

// ── UFC ──────────────────────────────────────────────────────────────────────
// Maps an ESPN MMA event's per-fight competitions into the standard game
// shape PickemMode already renders. Tier values drive roundPts so the
// existing roundPoints badge and grading logic both pick up the 5 tiers:
//   Prelim 1pt · Main Card 2pts · Co-Main 3pts · Main Event 5pts · Title 7pts
// Main card vs. prelim is detected by grouping fights on ESPN's per-fight
// `date` timestamp — UFC broadcasts encode the break as a multi-hour gap.

// "UFC 328" -> PPV via the regex; "UFC Freedom 250"-style events covered by
// the KNOWN_PPV_EVENT_IDS allowlist below since the regex doesn't match them;
// "UFC Fight Night: ..." or unnumbered -> Fight Night.
function isUFCPPV(eventName, eventId) {
  return KNOWN_PPV_EVENT_IDS.has(String(eventId)) || (!!eventName && /UFC\s+\d+/i.test(eventName))
}

// Composes a venue line like "Meta APEX, Las Vegas" from ESPN's venue
// object. Falls back to whatever subset is available.
function ufcVenueLabel(venue) {
  if (!venue) return ''
  const place  = venue.fullName || venue.name || ''
  const city   = venue.address?.city || ''
  const region = venue.address?.state || venue.address?.country || ''
  return [place, city, region].filter(Boolean).join(', ')
}

// Picks the next UFC event from the ESPN MMA scoreboard, considering
// today-or-future events within a 30-day window. Returns null if nothing
// is scheduled in that window. Used by both getUFCFights (for picks) and
// getNextUFCEvent (for the create/games card metadata).
async function fetchNextUFCEvent() {
  const res  = await fetch(`${API_URL}/api/espn/ufc/events`, { signal: AbortSignal.timeout(5000) })
  if (!res.ok) return null
  const data = await res.json()
  if (!data?.events?.length) return null
  const dayMs    = 24 * 60 * 60 * 1000
  const now      = Date.now()
  const horizon  = now + 30 * dayMs   // 30-day forward window
  const upcoming = data.events
    .filter(e => {
      const t = new Date(e.date).getTime()
      return t >= now - dayMs && t <= horizon
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
  return upcoming[0] || null
}

// Manual title-fight registry. ESPN's MMA feed doesn't reliably populate
// title-fight markers — verified live 2026-06-09 against Freedom 250, where
// the headline lightweight title bout (Topuria vs Gaethje) returned
// `comp.type.text: undefined`, `comp.type.id: "986"` (the lightweight weight-
// class id, not "3"), `comp.type.abbreviation: "Lightweight"`, and an empty
// `notes` array. All four automated detectors at the isTitleFight chain
// below resolve to false → fight scores 5 pts as a regular main event
// instead of the 7 it deserves. This Set is the fallback override.
//
// Keyed by ESPN competition.id (string, stable per fight). To add a title
// fight: hit the prod backend's events endpoint, find the competition.id of
// the title bout under event.competitions[], and add it here with an
// inline comment naming the fight.
//
//   curl https://wannabet-production-a85d.up.railway.app/api/espn/ufc/events
//
// Past comp IDs are harmless to leave in — a finished fight's id never
// re-enters getUFCFights' tier loop (filtered out as past event).
const TITLE_FIGHT_COMP_IDS = new Set([
  // UFC Freedom 250 (event 600058854, 2026-06-15)
  '401863575',  // Justin Gaethje vs Ilia Topuria — Lightweight title
])

// Manual PPV registry. The regex inside isUFCPPV ('/UFC\s+\d+/i') matches the
// standard "UFC <number>" pattern (UFC 318, UFC 320…) but fails on the
// occasional "UFC <Name> <number>" form ESPN ships — e.g. "UFC Freedom 250"
// has the word "Freedom" between "UFC" and "250", so the regex returns false
// and the event renders as Fight Night. Broadening the regex to allow
// arbitrary words between would also (incorrectly) flag historical
// "UFC on ESPN 24"-style Fight Nights as PPVs, so we opt for an ESPN
// event-id allowlist instead — narrow, explicit, no risk of false positives.
//
// Keyed by ESPN event.id (string, stable per event). To add a PPV: find the
// event.id from the prod backend's events endpoint and append here with the
// event name in the inline comment.
//
//   curl https://wannabet-production-a85d.up.railway.app/api/espn/ufc/events
//
// Display-only signal — isPPV/eventType are never persisted, never read by
// the grader, and only render the gold "PPV" / slate "Fight Night" badge.
const KNOWN_PPV_EVENT_IDS = new Set([
  '600058854',  // UFC Freedom 250 — 2026-06-15
])

// Per-event override: treat the ENTIRE fight list as the main card (no
// prelims). Use when an event is broadcast as a single card with no
// prelim → main-card split — every fight scores at least Main Card
// (2pts), with Co-Main (3pts) / Main Event (5pts) / Title (7pts) tiers
// still derived from position within the main-card subset. The default
// heuristic (latest broadcast-date bucket OR fallback to last-5 on a
// single-date card) still applies to events not in this set.
const ALL_MAIN_CARD_EVENT_IDS = new Set([
  '600058854',  // UFC Freedom 250 — 2026-06-15
])

// Main-Card-Only mode helpers moved to src/lib/mainCard.js so they can be
// imported by both the client (odds.js + PickemMode.jsx) and the server
// (gradePicksForUser backstop in server/index.js). Single source of truth
// for MAIN_CARD_COMP_IDS curation. Re-exported here so existing callers
// in this file's module graph keep working without import-path churn.
export { isMainCardEligible, hasMainCardDefinition } from './mainCard.js'

// Fetch a specific UFC event by id from the same scoreboard payload that
// fetchNextUFCEvent reads. Returns the raw ESPN event object (same shape
// fetchNextUFCEvent returns) for downstream tier mapping in getUFCFights.
// Returns null on network failure, empty scoreboard, or no-such-event —
// caller treats null as "no fights to render" (no implicit fallback to
// next-event; the loud-banner fallback lives in PickemMode for the
// undefined-eventId case only).
async function fetchUFCEventById(eventId) {
  const res = await fetch(`${API_URL}/api/espn/ufc/events`, { signal: AbortSignal.timeout(5000) })
  if (!res.ok) return null
  const data = await res.json()
  if (!data?.events?.length) return null
  return data.events.find(e => String(e.id) === String(eventId)) || null
}

// Distinguish the reasons a UFC pool's eventId fetches zero fights:
//   'in-events'        — eventId is in events[] (fights exist; getUFCFights
//                        returning [] would be a different bug)
//   'in-calendar-only' — eventId is in calendar[] but not events[] yet.
//                        Reassuring pre-card state — ESPN typically promotes
//                        cards from calendar[] to events[] ~1-2 weeks before
//                        fight night. PickemMode renders the calm "Picks
//                        open closer to fight week" banner.
//   'not-found'        — eventId is in neither. ONLY returned when ESPN
//                        successfully responded with both arrays present
//                        and the id was confirmed absent from each.
//                        Genuine drift; PickemMode renders the warn banner.
//   'unknown'          — couldn't check. Network error, timeout, 5xx, or
//                        missing eventId arg. Reserved separately so a
//                        transient blip doesn't render the alarming
//                        "event isn't on the schedule" banner on a
//                        genuinely-fine in-calendar-only pool. PickemMode
//                        treats 'unknown' identically to the pre-check
//                        null state — neutral "Checking event status…"
//                        placeholder. June 14 concurrent-load case: a
//                        timeout on a real pre-card pool would have
//                        previously falsely scared the user "your event
//                        is gone" — 'unknown' prevents that.
//
// Phase-2-carry-forward (cache consolidation) is now PRE-LAUNCH critical:
// this is the third helper hitting the uncached /api/espn/ufc/events
// proxy, and PickemMode's effect re-fires on the 60s games cadence for
// pre-card pools — per user, for days, under concurrent load. Move the
// short-TTL in-memory cache layer from "nice-to-have" to "before June 14."
export async function getUFCEventState(eventId) {
  if (!eventId) return 'unknown'
  try {
    const res = await fetch(`${API_URL}/api/espn/ufc/events`, { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return 'unknown'
    const data = await res.json()
    const inEvents = Array.isArray(data?.events)
      && data.events.some(e => String(e.id) === String(eventId))
    if (inEvents) return 'in-events'
    // Calendar path: leagues[N].calendar where league matches UFC by
    // slug/abbreviation. See getUpcomingEvents UFC branch for rationale
    // on find() vs [0]. Verified path 2026-06-03 PM.
    const ufcLeague = Array.isArray(data?.leagues)
      ? data.leagues.find(l => l?.slug === 'ufc' || l?.abbreviation === 'UFC')
      : null
    const inCalendar = Array.isArray(ufcLeague?.calendar)
      && ufcLeague.calendar.some(c => {
        const m = c.event?.$ref?.match(/\/events\/(\d+)/)
        return m && m[1] === String(eventId)
      })
    return inCalendar ? 'in-calendar-only' : 'not-found'
  } catch (err) {
    log('[UFC] getUFCEventState failed:', err?.message || err)
    return 'unknown'
  }
}

// UFC fights for the picker. When eventId is provided (post-Phase 4,
// from group.ufc_config.eventId), fetches that specific event's
// competitions[] and returns null if the event is no longer in the
// scoreboard. When eventId is undefined/null (legacy pools, the create-
// modal preview path, or ESPN-was-down-at-create gaps), falls back to
// fetchNextUFCEvent for backwards compatibility — the loud "Showing
// next event: <name>" banner in PickemMode surfaces this to the user
// so a fallback can't masquerade as the configured event silently.
// Tier logic (1/2/3/5/7) below is unchanged — it operates per-event
// and is correct on whichever event the fetch returned.
export async function getUFCFights(eventId) {
  try {
    const event = eventId
      ? await fetchUFCEventById(eventId)
      : await fetchNextUFCEvent()
    if (!event) return []

    const competitions = event.competitions || []
    const totalFights  = competitions.length
    const eventVenue   = ufcVenueLabel(event?.competitions?.[0]?.venue || event?.venue)
    const isPPV        = isUFCPPV(event.name, event.id)
    const eventType    = isPPV ? 'PPV' : 'Fight Night'
    const abbr = name => (name?.split(' ').pop() || '').slice(0, 3).toUpperCase()

    // Group fights by ESPN-side timestamp. Main card = latest-timestamp bucket;
    // prelims are everything earlier. UFC encodes the prelim → main card break
    // as a multi-hour broadcast gap. Single-date cards (rare Fight Night format
    // with no broadcast split) fall back to "last 5 fights = main card."
    const dateGroups = {}
    for (const c of competitions) {
      const date = c.date || ''
      if (!dateGroups[date]) dateGroups[date] = []
      dateGroups[date].push(c)
    }
    const uniqueDates = Object.keys(dateGroups).filter(d => d).sort()
    // Per-event override (ALL_MAIN_CARD_EVENT_IDS): every fight is on
    // the main card. Bypasses the broadcast-gap and last-5 heuristics.
    const forceAllMainCard = ALL_MAIN_CARD_EVENT_IDS.has(String(event.id))
    const mainCardComps =
      forceAllMainCard ? competitions
      : uniqueDates.length >= 2 ? dateGroups[uniqueDates[uniqueDates.length - 1]]
      : competitions.slice(-5)

    const fights = competitions.map((comp, index) => {
      const c1 = comp.competitors?.[0] || {}
      const c2 = comp.competitors?.[1] || {}
      // ESPN MMA usually omits homeAway; first listed is "home" by convention.
      const isHomeFirst = c1.homeAway === 'home' || c1.homeAway == null
      const homeC = isHomeFirst ? c1 : c2
      const awayC = isHomeFirst ? c2 : c1

      const homeName = homeC?.athlete?.displayName || homeC?.team?.displayName || 'Fighter'
      const awayName = awayC?.athlete?.displayName || awayC?.team?.displayName || 'Fighter'

      // Records live on the competitor, not on .athlete — ESPN exposes them
      // as records[0].summary (e.g. "28-1-0").
      const homeRecord = homeC?.records?.[0]?.summary || ''
      const awayRecord = awayC?.records?.[0]?.summary || ''

      // Tier detection. Title fight (7pts) trumps the Main Event slot —
      // a 5-round title fight is "Title Fight," not "Main Event." Main Card
      // (2pts) covers every main-card fight that isn't main/co-main/title,
      // distinguishing them from prelims (1pt) which broadcast on the
      // earlier card. Position-from-end within the main-card subset (not
      // the full competitions array) drives main-event/co-main detection.
      const mainCardIndex          = mainCardComps.indexOf(comp)
      const isMainCard             = mainCardIndex >= 0
      const isLastMainCardFight    = isMainCard && mainCardIndex === mainCardComps.length - 1
      const isSecondToLastMainCard = isMainCard && mainCardIndex === mainCardComps.length - 2
      const notesText = (comp.notes || [])
        .map(n => `${n?.headline || ''} ${n?.type || ''}`)
        .join(' ')
        .toLowerCase()
      const isTitleFight =
        comp.type?.text?.toLowerCase()?.includes('title') ||
        comp.type?.abbreviation?.toLowerCase()?.includes('title') ||
        notesText.includes('title') ||
        comp.type?.id === '3' ||
        TITLE_FIGHT_COMP_IDS.has(String(comp.id))
      const isMainEvent = !isTitleFight && (isLastMainCardFight || comp.format?.regulation?.periods === 5)
      // Display-only "Main Event" label gates on POSITION (last main-card fight)
      // not round count. Decouples the badge from the points tier so a 5-round
      // non-title second-to-last fight (e.g. Gane/Pereira on a multi-5R PPV)
      // can correctly render as "Co-Main Event" via isCoMain below WITHOUT
      // dropping the 5-pt scoring isMainEvent still produces at line 1017.
      // The points ternary intentionally keeps the existing isMainEvent test
      // (rounds===5 OR last-main-card) so the fail-safe 5-pt floor for any
      // 5-round non-title fight is preserved per the user-confirmed
      // Freedom 250 mapping 1/1/2/2/2/5/7. See HANDOFF "🟡 COSMETIC 2026-06-09
      // — duplicate 'Main Event' label" for the original analysis.
      const isMainEventLabel = !isTitleFight && isLastMainCardFight
      const isCoMain    = !isTitleFight && !isMainEventLabel && isSecondToLastMainCard

      // Chained ternary is intentional — exclusive by construction (first match wins),
      // so a 5-round title fight scores 7, never 7+5. DO NOT refactor to an additive
      // sum (isTitleFight*7 + isMainEvent*5 + ...) — that would double-count.
      const points    = isTitleFight ? 7 : isMainEvent ? 5 : isCoMain ? 3 : isMainCard ? 2 : 1
      const roundName = isTitleFight      ? 'Title Fight'
                       : isMainEventLabel ? 'Main Event'
                       : isCoMain         ? 'Co-Main Event'
                       : isMainCard       ? 'Main Card'
                       : 'Prelim'

      const startMs = new Date(comp.startDate || comp.date || event.date).getTime()
      const state   = comp.status?.type?.state || 'pre'
      const status  = state === 'in' ? 'live' : state === 'post' ? 'final' : 'upcoming'
      const completed = comp.status?.type?.completed || false
      // Informational only — the grader recomputes from score, but this lets
      // the UI show "Winner: X" without re-running the same comparison.
      const winnerName = completed
        ? (homeC?.winner ? homeName : awayC?.winner ? awayName : null)
        : null

      // Per-competition venue (preferred) falls back to the event-level one.
      const compVenue = ufcVenueLabel(comp.venue) || eventVenue

      // Weight class lives on comp.type for MMA (e.g. "Featherweight"); the
      // notes headline is a fallback for the "Title Fight" / "Co-Main" line
      // some events ship instead.
      const weightClass = comp.type?.abbreviation
                       || comp.type?.text
                       || comp.notes?.[0]?.headline
                       || ''

      // ESPN exposes the athlete id on either competitor.id or competitor.athlete.id
      // depending on the endpoint variant. Capture both so TeamComparison can
      // fetch per-fighter stats via /api/espn/ufc/athletes/:id. (Data fetch
      // only — no longer used for image URLs after the owned-badges refactor.)
      const homeAthleteId = homeC?.athlete?.id || homeC?.id || null
      const awayAthleteId = awayC?.athlete?.id || awayC?.id || null

      return {
        id: comp.id,
        sport: 'UFC',
        status,
        statusState:     state,
        statusCompleted: completed,
        homeScore: homeC?.score ?? null,
        awayScore: awayC?.score ?? null,
        round: null,
        roundAbbr: null,
        roundName,
        roundPts:    points,
        roundPoints: points,
        gameNumber:  null,
        seriesInfo:  weightClass || event.name || '',
        away: {
          team:      awayName,
          abbr:      abbr(awayName),
          // shortName ("A. Carnelossi" — first-initial + last name) is the
          // canonical compact label for the UFC name badge. Falls back to
          // displayName-based abbreviation if ESPN omits it for a fight.
          shortName: awayC?.athlete?.shortName || awayName,
          // ESPN's MMA scoreboard exposes athlete.flag.alt as a country
          // name ("USA", "Brazil", "Spain", "Georgia", …). Surfaced here
          // so FighterCorner can tint the card with the fighter's flag
          // palette instead of the generic red/blue corner colors.
          country:   awayC?.athlete?.flag?.alt || null,
          // Direct flag-image URL from ESPN's MMA scoreboard payload —
          // skip the alpha-3 → slug roundtrip the golf picker does.
          flagUrl:   awayC?.athlete?.flag?.href || null,
          score:     awayC?.score ?? null,
          record:    awayRecord,
          seed:      null,
        },
        home: {
          team:      homeName,
          abbr:      abbr(homeName),
          shortName: homeC?.athlete?.shortName || homeName,
          country:   homeC?.athlete?.flag?.alt || null,
          flagUrl:   homeC?.athlete?.flag?.href || null,
          score:     homeC?.score ?? null,
          record:    homeRecord,
          seed:      null,
        },
        startTime:   new Date(startMs).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' }),
        startTimeMs: startMs,
        commenceTime: comp.startDate || comp.date || event.date,
        spread: null, awayML: null, homeML: null, total: null,
        winner:      winnerName,
        homeAthleteId,
        awayAthleteId,
        eventName:   event.name || '',
        eventType,
        isPPV,
        venue:       compVenue,
        weightClass,
      }
    })

    // Main event first so the highest-value fight leads the card.
    return fights.sort((a, b) => b.roundPoints - a.roundPoints)
  } catch (err) {
    log('[UFC] getUFCFights failed:', err?.message || err)
    return []
  }
}

// Returns metadata for the next UFC event (within 30 days) — used by
// CreateGroupModal and the Games tab to render event cards without
// pulling the full fights array.
export async function getNextUFCEvent() {
  try {
    const event = await fetchNextUFCEvent()
    if (!event) return null
    const isPPV = isUFCPPV(event.name, event.id)
    return {
      id:          event.id,
      name:        event.name || 'UFC Event',
      date:        event.date,
      startTimeMs: new Date(event.date).getTime(),
      venue:       ufcVenueLabel(event?.competitions?.[0]?.venue || event?.venue),
      isPPV,
      eventType:   isPPV ? 'PPV' : 'Fight Night',
      fightCount:  event.competitions?.length || 0,
    }
  } catch (err) {
    log('[UFC] getNextUFCEvent failed:', err?.message || err)
    return null
  }
}

// ISO date string (YYYY-MM-DD) of the next UFC event — used by
// CreateGroupModal to set the group's start_date so PickemMode's per-day
// filter catches the fight card.
export async function getNextUFCEventDate() {
  const ev = await getNextUFCEvent()
  if (!ev?.date) return null
  return new Date(ev.date).toISOString().slice(0, 10)
}

// Forward-window event feed for the Home screen's Featured hero + (later)
// per-sport upcoming surfaces. Single source for "what's coming up" across
// team sports + UFC. For team sports, hits the existing proxy with the
// ?date=YYYYMMDD-YYYYMMDD range form (proxy maps the client's ?date= to
// ESPN's ?dates= so range queries work without server changes — grader
// already uses this at server/index.js:1165). For UFC, reuses the
// fetchNextUFCEvent infra (30-day window, full sorted event list).
//
// Returns: [{ id, sport, name, date (ms), venue, contestTypeDefault }]
//   - `name`: the real event/game name verbatim from ESPN (nominative use;
//     no logos, no endorsement framing — text only).
//   - `venue`: a friendly "venue, city, state" string when present.
//   - `contestTypeDefault`: the natural contest_type a Create Pool tap
//     should pre-fill ('pick_em' for everything today).
export async function getUpcomingEvents(sport, days = 14) {
  if (!sport) return []
  const sportKey = sport.toLowerCase()

  // UFC — merges ESPN's two upstream sources for the dropdown:
  //   events[]   — the ACTIVE card (Muhammad/Bonfim today). Has full
  //                competitions[] for picker fetch.
  //   calendar[] — the rest of the season as references-only entries
  //                {label, startDate, endDate, event:{$ref}}. No
  //                competitions; the id is embedded in the $ref URL
  //                path under /events/<digits>.
  //
  // Diagnostic 2026-06-03 PM (UFC build phase 4): id from
  // calendar.event.$ref always matches the same event's events[].id
  // after ESPN promotes the card. So a pool created today against a
  // calendar-only event will reconcile correctly once ESPN promotes
  // it — the picker's fetchUFCEventById hits events[] only and stays
  // empty until promotion, at which point the same id resolves.
  //
  // Critical guards:
  //   (a) $ref regex MUST yield an id, otherwise EXCLUDE the entry —
  //       a null-eventId pool can never resolve to fights.
  //   (b) Dedupe by id, events[] WINS — during the promotion window
  //       an event can appear in both arrays; we want the version
  //       with competitions[]. Map-based dedupe ensures insertion
  //       order doesn't matter; the events[] pass overwrites the
  //       calendar[] pass explicitly.
  if (sport === 'UFC') {
    try {
      const res = await fetch(`${API_URL}/api/espn/ufc/events`, { signal: AbortSignal.timeout(5000) })
      if (!res.ok) return []
      const data = await res.json()
      const dayMs    = 24 * 60 * 60 * 1000
      const now      = Date.now()
      const horizon  = now + days * dayMs

      const fromEventsEntry = e => ({
        id:                  String(e.id),
        sport:               'UFC',
        name:                e.name || 'UFC event',
        date:                new Date(e.date).getTime(),
        venue:               ufcVenueLabel(e?.competitions?.[0]?.venue || e?.venue),
        hasCompetitions:     Array.isArray(e.competitions) && e.competitions.length > 0,
        contestTypeDefault:  'pick_em',
      })

      // Calendar entries: id from /events/<digits> path in event.$ref.
      // $ref points at sports.core.api.espn.pvt (private subdomain) —
      // DO NOT fetch the URL; we extract the id from the path only.
      // Returns null on regex miss → filtered out before merge.
      const fromCalendarEntry = c => {
        const m = c.event?.$ref?.match(/\/events\/(\d+)/)
        if (!m) return null
        return {
          id:                  String(m[1]),
          sport:               'UFC',
          name:                c.label || 'UFC event',
          date:                new Date(c.startDate).getTime(),
          venue:               null,   // calendar entries have no venue
          hasCompetitions:     false,  // calendar entries are pre-card by definition
          contestTypeDefault:  'pick_em',
        }
      }

      const eventsList   = Array.isArray(data?.events)
        ? data.events.map(fromEventsEntry)
        : []
      // Calendar lives at leagues[N].calendar where the league matches
      // UFC by abbreviation/slug — NOT at top-level data.calendar.
      // Verified against Railway's live proxy response 2026-06-03 PM:
      // data.leagues is length 1 with slug "ufc" / abbreviation "UFC"
      // carrying the 25-entry calendar. Using find() rather than [0]
      // keeps the access robust if ESPN ever expands the leagues
      // array in this endpoint.
      const ufcLeague = Array.isArray(data?.leagues)
        ? data.leagues.find(l => l?.slug === 'ufc' || l?.abbreviation === 'UFC')
        : null
      const calendarList = Array.isArray(ufcLeague?.calendar)
        ? ufcLeague.calendar.map(fromCalendarEntry).filter(Boolean)
        : []

      // Map-based dedupe — calendar entries seed the map first, then
      // events[] entries overwrite any colliding id. The events[]
      // entry (hasCompetitions: true) always wins regardless of
      // insertion order in the response.
      const byId = new Map()
      for (const c of calendarList) byId.set(c.id, c)
      for (const e of eventsList)   byId.set(e.id, e)

      return [...byId.values()]
        .filter(entry => entry.date >= now - dayMs && entry.date <= horizon)
        .sort((a, b) => a.date - b.date)
    } catch (err) {
      log('getUpcomingEvents UFC failed:', err?.message || err)
      return []
    }
  }

  // Team sports — single proxy call with date range. ESPN's `?dates=A-B`
  // returns all events scheduled in the window; we filter to "starts in
  // the future" so games that already locked don't show as upcoming.
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = new Date(today)
  end.setDate(end.getDate() + days)
  const fmt = d => d.toISOString().slice(0, 10).replace(/-/g, '')
  const dateRange = `${fmt(today)}-${fmt(end)}`

  try {
    const res = await fetch(
      `${API_URL}/api/espn/${sportKey}/scoreboard?date=${dateRange}`,
      { signal: AbortSignal.timeout(5000) }
    )
    if (!res.ok) return []
    const data = await res.json()
    if (!data?.events?.length) return []
    const nowMs = Date.now()
    return data.events
      .filter(e => {
        const t = new Date(e.date || e?.competitions?.[0]?.date || 0).getTime()
        return t >= nowMs
      })
      .sort((a, b) =>
        new Date(a.date || a?.competitions?.[0]?.date || 0) -
        new Date(b.date || b?.competitions?.[0]?.date || 0)
      )
      .map(e => {
        const comp = e?.competitions?.[0]
        const home = comp?.competitors?.find(c => c.homeAway === 'home')
        const away = comp?.competitors?.find(c => c.homeAway === 'away')
        const venue = comp?.venue
        const venueLabel = venue
          ? [venue.fullName || venue.name, venue.address?.city, venue.address?.state]
              .filter(Boolean).join(', ')
          : ''
        const homeName = away?.team?.displayName ? null : null  // unused; sorted by name below
        return {
          id:                  e.id,
          sport,
          name:                e.name
                              || (away?.team?.displayName && home?.team?.displayName
                                    ? `${away.team.displayName} @ ${home.team.displayName}`
                                    : 'Upcoming event'),
          date:                new Date(e.date || comp?.date || 0).getTime(),
          venue:               venueLabel,
          contestTypeDefault:  'pick_em',
        }
      })
  } catch (err) {
    log('getUpcomingEvents failed:', err?.message || err)
    return []
  }
}

// Fetch the golf tournament schedule from the server-side proxy backed by
// MockGolfProvider (today) or DataGolfProvider (Stage 8 with subscription).
// Mirrors the fetchNextUFCEvent pattern: 5s timeout, null on error, returns
// the array unchanged so callers can render an empty list / single-item
// list / future full-schedule list without special-casing.
export async function fetchGolfSchedule() {
  try {
    const res = await fetch(`${API_URL}/api/golf/schedule`, { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data?.events) ? data.events : []
  } catch (err) {
    log('[golf] fetchGolfSchedule failed:', err?.message || err)
    return []
  }
}

// Stage 5 — per-round pickability for the golf picker's round selector.
// Returns the {round:status} map ({} on error) so the UI can gate which
// rounds are tappable BEFORE fetching pairings for them.
// Event-level metadata for the pool create flow — primarily
// dateStart + dateEnd which feed src/lib/golfPoolDates.js's
// computeGolfPoolDates. Returns null on any failure so the caller
// can fall back to the +5-day heuristic without crashing the create.
export async function fetchGolfEventMeta(eventId) {
  if (!eventId) return null
  try {
    const url = `${API_URL}/api/golf/event-meta?eventId=${encodeURIComponent(eventId)}`
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return null
    return await res.json()
  } catch (err) {
    log('[golf] fetchGolfEventMeta failed:', err?.message || err)
    return null
  }
}

export async function fetchGolfRoundStatus(eventId) {
  if (!eventId) return {}
  try {
    const url = `${API_URL}/api/golf/round-status?eventId=${encodeURIComponent(eventId)}`
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return {}
    const data = await res.json()
    return (data?.status && typeof data.status === 'object') ? data.status : {}
  } catch (err) {
    log('[golf] fetchGolfRoundStatus failed:', err?.message || err)
    return {}
  }
}

/**
 * Fetch live in-progress scores for a golf round. Display-only — the grader
 * uses its own server-side path with the round-completeness gate; this is
 * a parallel read for the picker's provisional view.
 *
 * Returns `{ scores, fetchedAt, fetchError }`. `fetchError` is non-null
 * when the fetch itself failed (network/timeout/non-2xx) — callers should
 * distinguish that from a clean empty response (silent-empty fallbacks
 * already cost us once, see the Pairings TBA incident 2026-06-11).
 *
 * @param {string|number} eventId
 * @param {number} round 1-4
 */
// Fetches the per-golfer per-round score map for a tournament's R1-R4 strip
// (GolfPicksMode.jsx). Server-side endpoint /api/golf/round-scores does 4
// sequential live-stats calls; same focus guards as fetchGolfLiveScores.
//
// Returns an empty bundle on any error so the picker keeps working without
// special-casing — the R1-R4 strip falls back to its existing dash treatment.
//
// Shape (matches server):
//   scoresByDg: { <dgId>: { 1?: score|null, 2?: score|null, 3?: score|null, 4?: score|null } }
//   rounds:     { 1: 'final'|'in_progress'|'no_data', ... }
//   fetchedAt:  ISO string | null
//   fetchError: null on success, message on failure
export async function fetchGolfRoundScores(eventId) {
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  const empty = { scoresByDg: {}, rounds: {}, fetchedAt: null, fetchError: null }
  if (!eventId) {
    return { ...empty, fetchError: 'invalid input' }
  }
  try {
    const url = `${API_URL}/api/golf/round-scores?eventId=${encodeURIComponent(eventId)}`
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) })
    if (!res.ok) return { ...empty, fetchError: `HTTP ${res.status}` }
    const data = await res.json()
    return {
      scoresByDg: (data?.scoresByDg && typeof data.scoresByDg === 'object') ? data.scoresByDg : {},
      rounds:     (data?.rounds     && typeof data.rounds     === 'object') ? data.rounds     : {},
      fetchedAt:  typeof data?.fetchedAt === 'string' ? data.fetchedAt : null,
      fetchError: null,
    }
  } catch (err) {
    log('[golf] fetchGolfRoundScores failed:', err?.message || err)
    return { ...empty, fetchError: err?.message || 'fetch failed' }
  }
}

export async function fetchGolfLiveScores(eventId, round) {
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  const empty = { scores: [], fetchedAt: null, fetchError: null }
  if (!eventId || !Number.isInteger(round)) {
    return { ...empty, fetchError: 'invalid input' }
  }
  try {
    const url = `${API_URL}/api/golf/live-scores?eventId=${encodeURIComponent(eventId)}&round=${round}`
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) })
    if (!res.ok) return { ...empty, fetchError: `HTTP ${res.status}` }
    const data = await res.json()
    return {
      scores:     Array.isArray(data?.scores) ? data.scores : [],
      fetchedAt:  typeof data?.fetchedAt === 'string' ? data.fetchedAt : null,
      fetchError: null,
    }
  } catch (err) {
    log('[golf] fetchGolfLiveScores failed:', err?.message || err)
    return { ...empty, fetchError: err?.message || 'fetch failed' }
  }
}

// Phase 3 — divisions tier snapshot. Fetches the frozen-at-create
// 6-tier odds-banded snapshot from the server (which composes
// pre-tournament + field-updates + the banding helpers).
//
// ERROR-SHAPE CONTRACT (operator-locked, 2026-06-13). Every documented
// server state must surface as a STRUCTURED error to the caller — NO
// silent-empty fallback (the lesson from "Pairings TBA"). The create
// flow MUST block pool creation on any failure; the user can never
// proceed to freeze a partial snapshot.
//
// Returns on success:
//   { ok: true, snapshot, playerLookup, eventId, eventName, stats,
//     fetchedAt }
//
// Returns on failure (every documented HTTP status surfaces with code +
// detail intact so the create-flow modal can render a per-code message):
//   { ok: false, error: { code, status, detail, ...extras } }
//
// Codes (matching server/index.js /api/golf/divisions/tier-snapshot):
//   400  → 'BAD_REQUEST'              missing eventId (client bug)
//   409  → 'EVENT_MISMATCH'           requested eventId !== DataGolf's
//                                     currently-served event_id
//   409  → 'EVENT_NAME_MISMATCH'      pre-tournament + field-updates
//                                     disagree on event identity
//   422  → 'FIELD_TOO_SMALL'          field < 51 (T6 would be empty)
//   503  → 'FIELD_INCOMPLETE'         some field entrants not priced;
//                                     extras: fieldSize, pricedCount,
//                                     unpricedCount, unpricedSample
//   502  → 'UPSTREAM_FAILED'          DataGolf fetch/parse failure
//                                     (collapses field_updates_failed,
//                                     pre_tournament_failed, etc.)
//   other → 'NETWORK_FAILED'          fetch threw / non-2xx unmapped
export async function fetchTierSnapshot(eventId) {
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  if (!eventId) {
    return {
      ok:    false,
      error: { code: 'BAD_REQUEST', status: 400, detail: 'eventId is required' },
    }
  }
  try {
    const url = `${API_URL}/api/golf/divisions/tier-snapshot?eventId=${encodeURIComponent(eventId)}`
    const res = await fetch(url, { signal: AbortSignal.timeout(15000) })
    const data = await res.json().catch(() => null)

    if (!res.ok) {
      // Map server `error` string into the contract codes above.
      const serverError = (data && typeof data.error === 'string') ? data.error : null
      let code = 'NETWORK_FAILED'
      if (serverError === 'FIELD_INCOMPLETE')       code = 'FIELD_INCOMPLETE'
      else if (serverError === 'FIELD_TOO_SMALL')   code = 'FIELD_TOO_SMALL'
      else if (serverError === 'EVENT_MISMATCH')    code = 'EVENT_MISMATCH'
      else if (serverError === 'EVENT_NAME_MISMATCH') code = 'EVENT_NAME_MISMATCH'
      else if (res.status === 502 || res.status === 503) {
        // 502 family from the server collapses into UPSTREAM_FAILED unless
        // it was the specific FIELD_INCOMPLETE 503 (handled above).
        code = 'UPSTREAM_FAILED'
      }
      else if (res.status === 400) code = 'BAD_REQUEST'
      return {
        ok:    false,
        error: {
          code,
          status:        res.status,
          detail:        data?.detail || `HTTP ${res.status}`,
          // Preserve extras for the picker's per-code messaging.
          fieldSize:     data?.fieldSize,
          pricedCount:   data?.pricedCount,
          unpricedCount: data?.unpricedCount,
          unpricedSample: data?.unpricedSample,
          eventName:        data?.eventName,
          servingEventId:   data?.servingEventId,
          servingEventName: data?.servingEventName,
        },
      }
    }

    // 2xx — defensively validate the shape before declaring success.
    // A successful HTTP status with malformed body is still a failure
    // for the create-flow's purposes.
    if (!data || typeof data !== 'object' ||
        typeof data.snapshot !== 'object' || data.snapshot === null) {
      return {
        ok:    false,
        error: { code: 'NETWORK_FAILED', status: res.status, detail: 'malformed snapshot response' },
      }
    }
    return {
      ok:           true,
      snapshot:     data.snapshot,
      playerLookup: data.playerLookup || {},
      eventId:      data.eventId,
      eventName:    data.eventName,
      stats:        data.stats || null,
      fetchedAt:    data.fetchedAt || null,
    }
  } catch (err) {
    log('[golf] fetchTierSnapshot failed:', err?.message || err)
    return {
      ok:    false,
      error: { code: 'NETWORK_FAILED', status: 0, detail: err?.message || 'fetch failed' },
    }
  }
}

// Phase 3.5 (deferred snapshot, Option A) — attempt to lazy-freeze the
// tier snapshot for a divisions pool currently in 'awaiting_odds'
// state. JWT-authenticated POST to the server's freeze-snapshot route
// (server/index.js, gated by requireAuth + group membership check +
// the freeze_division_snapshot_atomic RPC's race-safe SELECT FOR
// UPDATE — migration 037).
//
// The server is idempotent + cheap when nothing has changed: the
// pre-fetch status check short-circuits already-frozen pools before
// any DataGolf call. Multi-opener races collapse from N fetches
// toward 1-2 (operator refinement 2, 2026-06-13).
//
// Returns a structured result — never throws. NO silent-empty
// fallback (same discipline as fetchTierSnapshot):
//
//   { ok: true,
//     status:  'frozen' | 'still_awaiting',
//     golfConfig: {...},     // the post-call golf_config from server
//     error?: { code, detail } }  -- present only when still_awaiting
//
//   { ok: false,
//     error: { code, status?, detail } }
//
// Status meanings:
//   'frozen'         — golf_config has tierSnapshot + tierPlayerLookup
//                      now; the picker should switch into roster mode
//   'still_awaiting' — odds STILL not out (or field still firming);
//                      caller should keep showing the awaiting-odds
//                      placeholder; error.code is the underlying
//                      buildTierSnapshot signal (EVENT_MISMATCH,
//                      FIELD_INCOMPLETE, EVENT_NAME_MISMATCH)
//
// Failure codes (ok:false) mirror fetchTierSnapshot's contract for
// hard errors, plus auth/membership:
//   UNAUTHENTICATED  — no Supabase session
//   NOT_A_MEMBER     — caller not in group_members for this pool (403)
//   GROUP_NOT_FOUND  — no such groupId (404)
//   NOT_DIVISIONS_POOL — wrong contest_type/sport (409)
//   FIELD_TOO_SMALL  — hard event-eligibility error (422)
//   UPSTREAM_FAILED  — DataGolf 502/503 family
//   NETWORK_FAILED   — fetch threw / non-2xx unmapped
//
// The freeze-on-mount throttle lives in the picker (GolfDivisionsMode);
// this wrapper itself is unthrottled so the "Check now" button can
// bypass.
export async function attemptFreezeSnapshot(groupId) {
  if (!groupId) {
    return { ok: false, error: { code: 'BAD_REQUEST', detail: 'groupId required' } }
  }
  if (!supabase) {
    return { ok: false, error: { code: 'UNAUTHENTICATED', detail: 'Supabase not configured' } }
  }
  let token = null
  try {
    const { data } = await supabase.auth.getSession()
    token = data?.session?.access_token || null
  } catch (err) {
    log('[freeze-snapshot] session read failed:', err?.message || err)
  }
  if (!token) {
    return { ok: false, error: { code: 'UNAUTHENTICATED', detail: 'No active session' } }
  }
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  const url = `${API_URL}/api/golf/divisions/groups/${encodeURIComponent(groupId)}/freeze-snapshot`
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal:  AbortSignal.timeout(20_000),
    })
    const data = await res.json().catch(() => null)

    if (res.ok) {
      // 200 path: either 'frozen' (RPC ran or short-circuit) or
      // 'still_awaiting' (build returned EVENT_MISMATCH/FIELD_INCOMPLETE
      // /EVENT_NAME_MISMATCH — server maps those to 200 by design).
      if (!data || typeof data !== 'object') {
        return { ok: false, error: { code: 'NETWORK_FAILED', status: res.status, detail: 'malformed response' } }
      }
      if (data.status === 'frozen') {
        return { ok: true, status: 'frozen', golfConfig: data.golf_config || null }
      }
      if (data.status === 'still_awaiting') {
        return {
          ok:         true,
          status:     'still_awaiting',
          golfConfig: data.golf_config || null,
          error:      data.error || null,
        }
      }
      return { ok: false, error: { code: 'NETWORK_FAILED', status: res.status, detail: `unknown status: ${data.status}` } }
    }

    // Non-2xx — map server error codes.
    const serverCode = (data && typeof data.error === 'string') ? data.error : null
    let code = 'NETWORK_FAILED'
    if (serverCode === 'NOT_A_MEMBER')          code = 'NOT_A_MEMBER'
    else if (serverCode === 'GROUP_NOT_FOUND')  code = 'GROUP_NOT_FOUND'
    else if (serverCode === 'NOT_DIVISIONS_POOL') code = 'NOT_DIVISIONS_POOL'
    else if (serverCode === 'FIELD_TOO_SMALL')  code = 'FIELD_TOO_SMALL'
    else if (serverCode === 'NO_EVENT_ID')      code = 'NO_EVENT_ID'
    else if (res.status === 401)                code = 'UNAUTHENTICATED'
    else if (res.status === 502 || res.status === 503) code = 'UPSTREAM_FAILED'
    return {
      ok:    false,
      error: {
        code,
        status: res.status,
        detail: data?.detail || `HTTP ${res.status}`,
      },
    }
  } catch (err) {
    log('[freeze-snapshot] fetch failed:', err?.message || err)
    return {
      ok:    false,
      error: { code: 'NETWORK_FAILED', status: 0, detail: err?.message || 'fetch failed' },
    }
  }
}

// Cross-member divisions picks reveal (Slice 1). Wraps
// GET /api/groups/:groupId/members/:userId/picks. The endpoint applies
// the tee-time gate server-side; this wrapper just bears the JWT and
// normalizes the response shape.
//
// Return shape:
//   { picks: [{id, tier, revealed, selected_dg_id, selected_name,
//              total_strokes, result}, ...], reason?: string, error: string|null }
//   - Revealed picks have full identity + score.
//   - Hidden picks have revealed:false and identity/score nulled.
//   - reason='lock_state_unavailable' when DataGolf is unreachable
//     (server returns all picks masked); client should treat as
//     "we can't tell — try again" rather than silent emptiness.
//
// Fail-open on error: returns picks:[] so the existing renderer's
// "0 picks" empty state shows. Matches loadDivisionGradedPicks' posture.
//
// Callers MUST branch on viewer-vs-target before calling this — for
// self-views, loadDivisionGradedPicks reads via own-only RLS directly
// (faster, no server roundtrip, and the gate would incorrectly hide
// the owner's own pre-tee picks).
export async function fetchMemberDivisionPicks(groupId, targetUserId) {
  if (!groupId || !targetUserId) return { picks: [], error: 'BAD_REQUEST' }
  if (!supabase) return { picks: [], error: 'UNAUTHENTICATED' }
  let token = null
  try {
    const { data } = await supabase.auth.getSession()
    token = data?.session?.access_token || null
  } catch (err) {
    log('[member-picks] session read failed:', err?.message || err)
  }
  if (!token) return { picks: [], error: 'UNAUTHENTICATED' }
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  const url = `${API_URL}/api/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(targetUserId)}/picks`
  try {
    const res = await fetch(url, {
      method:  'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal:  AbortSignal.timeout(10_000),
    })
    if (!res.ok) return { picks: [], error: `HTTP_${res.status}` }
    const data = await res.json().catch(() => null)
    return {
      picks:  Array.isArray(data?.picks) ? data.picks : [],
      reason: data?.reason || null,
      error:  null,
    }
  } catch (err) {
    log('[member-picks] fetch failed:', err?.message || err)
    return { picks: [], error: 'NETWORK_FAILED' }
  }
}

// Slice B — server-side divisions save. Wraps
// POST /api/groups/:groupId/division-picks. The endpoint enforces:
//   - freemium lock (pool unlocked_at IS NOT NULL)
//   - shape + snapshot validation (snapshot server-resolved, ignores
//     any client-provided tierSnapshot — closes Phase 4 hardening gap)
//   - tee-time gate (all-or-nothing; per-tier copy-protection +
//     tier-lock with idempotent exact-resave bypass)
//   - DataGolf outage fallback (coarse calendar-day check, degraded
//     mode returns reason='degraded_lock_state')
//
// Return shape: { ok: true } on success | { ok: false, error: { code,
// detail } } on failure, matching the existing saveDivisionPicks
// contract so callers don't change.
//
// Special error codes propagated from the endpoint:
//   POOL_LOCKED              — pool unlocked_at IS NULL
//   NOT_A_MEMBER             — caller is not a group member
//   LOCKED_TIERS             — one or more tiers have teed-off golfers;
//                              `.error.lockedTiers` contains the array
//   LOCK_STATE_DEGRADED      — DataGolf outage + event has started
//   LOCK_STATE_UNKNOWN       — DataGolf outage + no eventStartDate
//   NO_SNAPSHOT              — pool not frozen yet
//   INVALID_*                — shape / snapshot validation failed
export async function submitMemberDivisionPicks(groupId, picks, tieGuess) {
  if (!groupId) {
    return { ok: false, error: { code: 'BAD_REQUEST', detail: 'groupId required' } }
  }
  if (!supabase) {
    return { ok: false, error: { code: 'UNAUTHENTICATED', detail: 'Supabase not configured' } }
  }
  let token = null
  try {
    const { data } = await supabase.auth.getSession()
    token = data?.session?.access_token || null
  } catch (err) {
    log('[division-picks-save] session read failed:', err?.message || err)
  }
  if (!token) {
    return { ok: false, error: { code: 'UNAUTHENTICATED', detail: 'No active session' } }
  }
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  const url = `${API_URL}/api/groups/${encodeURIComponent(groupId)}/division-picks`
  try {
    const res = await fetch(url, {
      method:  'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body:    JSON.stringify({ picks, tieGuess }),
      signal:  AbortSignal.timeout(15_000),
    })
    const data = await res.json().catch(() => null)
    if (res.ok && data?.ok) {
      return { ok: true, written: data.written, degradedMode: data.degradedMode }
    }
    // Failure path — preserve the server's error code + detail so the
    // existing client error-handling branches still work.
    const code   = data?.error  || `HTTP_${res.status}`
    const detail = data?.detail || `HTTP ${res.status}`
    const error  = { code, detail }
    // Pass through structured fields for richer client rendering when
    // they exist (locked-tier list, degraded reason).
    if (Array.isArray(data?.lockedTiers)) error.lockedTiers = data.lockedTiers
    if (data?.reason)                    error.reason       = data.reason
    return { ok: false, error }
  } catch (err) {
    log('[division-picks-save] fetch failed:', err?.message || err)
    return { ok: false, error: { code: 'NETWORK_FAILED', detail: err?.message || 'fetch failed' } }
  }
}

// Stage 5 — pairings for a specific (event, round). Returns a
// PAIRINGS BUNDLE { pairings, tzOffsetHours } (empty bundle on error)
// so callers can render an empty state without special-casing.
// tzOffsetHours is event-level (same value for every round); it's
// returned per call for caller-side simplicity — the client cache
// only needs to hold one bundle per (eventId, round) and discovers
// the tzOffset alongside.
//
// Server-side route validates round 1-4; out-of-range calls here
// would 400, which we surface as an empty bundle.
export async function fetchGolfPairings(eventId, round) {
  const empty = { pairings: [], tzOffsetHours: 0 }
  if (!eventId || !Number.isInteger(round)) return empty
  try {
    const url = `${API_URL}/api/golf/pairings?eventId=${encodeURIComponent(eventId)}&round=${round}`
    const res = await fetch(url, { signal: AbortSignal.timeout(12000) })
    if (!res.ok) return empty
    const data = await res.json()
    return {
      pairings:      Array.isArray(data?.pairings) ? data.pairings : [],
      tzOffsetHours: typeof data?.tzOffsetHours === 'number' ? data.tzOffsetHours : 0,
    }
  } catch (err) {
    log('[golf] fetchGolfPairings failed:', err?.message || err)
    return empty
  }
}

export async function getPlayoffGames(sport) {
  const API_URL = import.meta.env.VITE_API_URL ||
    'http://localhost:3001'
  const sportKey = sport?.toLowerCase() || 'nba'

  try {
    const url = `${API_URL}/api/espn/${sportKey}/scoreboard`
    const res = await fetch(url, { signal: AbortSignal.timeout(10000) })
    const data = await res.json()
    if (!data?.events?.length) return []

    const roundPoints = {
      'RD2': 8, 'RD4': 4, 'RD8': 2, 'RD16': 1
    }
    const roundNames = {
      'RD2': 'Finals',
      'RD4': 'Conference Finals',
      'RD8': 'Second Round',
      'RD16': 'First Round'
    }

    return data.events.map(e => {
      const comp = e.competitions?.[0]
      const home = comp?.competitors?.find(
        c => c.homeAway === 'home')
      const away = comp?.competitors?.find(
        c => c.homeAway === 'away')
      const roundAbbr = comp?.type?.abbreviation || 'RD16'

      return {
        id: e.id,
        sport,
        home: home?.team?.displayName || '',
        away: away?.team?.displayName || '',
        // Scores + completion flags carried through so resolvePicks can
        // grade picks against playoff finals (previously dropped).
        homeScore:       home?.score ?? null,
        awayScore:       away?.score ?? null,
        statusCompleted: comp?.status?.type?.completed || false,
        homeSeed: home?.seed || null,
        awaySeed: away?.seed || null,
        time: new Date(e.date).toLocaleTimeString(
          'en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short'
          }),
        commenceTime: e.date,
        status: comp?.status?.type?.state || 'pre',
        statusDetail: comp?.status?.type?.shortDetail || '',
        roundAbbr,
        roundPoints: roundPoints[roundAbbr] || 1,
        roundName: roundNames[roundAbbr] || 'Playoffs',
        seriesRecord: e.notes?.[0]?.headline || '',
        gameNumber: e.notes?.[0]?.headline?.match(
          /Game \d+/)?.[0] || '',
        isPlayoff: true,
        awayML: null,
        homeML: null,
        spread: null,
        total: null,
      }
    }).filter(g => g.home && g.away)

  } catch (err) {
    log('getPlayoffGames failed:', err)
    return []
  }
}
